import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import { Phone, Menu, X, ChevronRight } from 'lucide-react';

const NAV_LINKS = [
  { label: 'Services', to: '/services' },
  { label: 'Projects', to: '/projects' },
  { label: 'About', to: '/about' },
  { label: 'Journal', to: '/journal' },
  { label: 'Estimate', to: '/estimate' },
  { label: 'Contact', to: '/contact' },
];

function LogoMark({ size = 38 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 80 80" className="flex-shrink-0">
      <path d="M10 5 L10 75" stroke="#E8A020" strokeWidth="3" fill="none" strokeLinecap="round" />
      <path d="M70 5 L70 75" stroke="#E8A020" strokeWidth="3" fill="none" strokeLinecap="round" />
      <path d="M10 40 L70 40" stroke="#E8A020" strokeWidth="3" fill="none" strokeLinecap="round" />
      <path d="M10 5 L40 5" stroke="#E8A020" strokeWidth="2" fill="none" strokeLinecap="round" />
      <path d="M50 5 L70 15" stroke="#E8A020" strokeWidth="2" fill="none" strokeLinecap="round" />
    </svg>
  );
}

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handle = () => setScrolled(window.scrollY > 70);
    window.addEventListener('scroll', handle, { passive: true });
    return () => window.removeEventListener('scroll', handle);
  }, []);

  useEffect(() => { setMobileOpen(false); }, [location]);

  return (
    <>
      <motion.nav
        data-testid="navbar"
        className="fixed top-0 left-0 right-0 z-[200] flex items-center justify-between h-[72px] lg:h-[72px] px-5 lg:px-10 transition-all duration-500"
        style={{
          background: scrolled ? 'rgba(5,6,8,0.90)' : 'transparent',
          backdropFilter: scrolled ? 'blur(28px) saturate(160%)' : 'none',
          borderBottom: scrolled ? '1px solid rgba(232,160,32,0.12)' : '1px solid transparent',
        }}
        initial={{ y: -72, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
      >
        {/* Left - Logo */}
        <Link to="/" className="flex items-center gap-3 no-underline" data-testid="navbar-logo">
          <LogoMark size={34} />
          <div className="flex flex-col">
            <span className="font-sans font-bold text-white text-sm tracking-[0.12em]">HOLISTIC</span>
            <span className="font-sans font-normal text-[9px] tracking-[0.18em] uppercase" style={{ color: '#E8A020' }}>
              General Trading Works PLC
            </span>
          </div>
          <div className="hidden lg:flex items-center ml-3 pl-3" style={{ borderLeft: '1px solid rgba(232,160,32,0.15)' }}>
            <span className="font-sans font-medium text-[9px] tracking-[0.15em] uppercase" style={{ color: 'rgba(232,160,32,0.55)' }}>
              &#9792; Women-Owned
            </span>
          </div>
        </Link>

        {/* Center - Nav Links (desktop) */}
        <div className="hidden lg:flex items-center gap-10">
          {NAV_LINKS.map(link => {
            const active = location.pathname === link.to || (link.to !== '/' && location.pathname.startsWith(link.to));
            return (
              <Link
                key={link.to}
                to={link.to}
                data-testid={`nav-link-${link.label.toLowerCase()}`}
                className="relative font-sans font-medium text-[13px] tracking-[0.04em] no-underline transition-colors duration-200"
                style={{ color: active ? '#fff' : 'rgba(255,255,255,0.65)' }}
              >
                {active && (
                  <motion.div
                    layoutId="nav-dot"
                    className="absolute -top-3 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full"
                    style={{ background: '#E8A020' }}
                    transition={{ type: 'spring', stiffness: 220, damping: 18 }}
                  />
                )}
                {link.label}
                {active && (
                  <motion.div
                    layoutId="nav-underline"
                    className="absolute -bottom-1 left-0 right-0 h-[1.5px]"
                    style={{ background: '#E8A020' }}
                    transition={{ type: 'spring', stiffness: 220, damping: 18 }}
                  />
                )}
              </Link>
            );
          })}
        </div>

        {/* Right - Phone + CTA */}
        <div className="hidden lg:flex items-center gap-3">
          <a
            href="tel:+251911146950"
            data-testid="navbar-phone"
            className="font-sans font-medium text-[12px] tracking-[0.04em] no-underline transition-all duration-200 hover:text-white"
            style={{ color: 'rgba(232,160,32,0.6)' }}
          >
            +251 91 114 6950
          </a>
          <Link
            to="/estimate"
            data-testid="navbar-cta"
            className="relative font-sans font-bold text-[11px] tracking-[0.1em] uppercase no-underline px-5 py-2.5 transition-all duration-200 hover:bg-amber-bright"
            style={{ background: '#E8A020', color: '#050608' }}
          >
            Get Estimate
          </Link>
        </div>

        {/* Mobile hamburger */}
        <button
          data-testid="navbar-mobile-toggle"
          className="lg:hidden flex items-center justify-center w-10 h-10"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="Toggle menu"
        >
          {mobileOpen ? <X size={24} color="#E8A020" /> : <Menu size={24} color="#fff" />}
        </button>
      </motion.nav>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            data-testid="mobile-menu"
            className="fixed inset-0 z-[199] flex"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="absolute inset-0" style={{ background: '#050608' }}>
              <div className="absolute inset-0 blueprint-grid-overlay" style={{ opacity: 0.06 }} />
            </div>
            <div className="relative flex flex-col w-[60%] pt-24 px-8">
              {NAV_LINKS.map((link, i) => (
                <motion.div
                  key={link.to}
                  initial={{ y: 50, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: i * 0.07, type: 'spring', stiffness: 65, damping: 14 }}
                >
                  <Link
                    to={link.to}
                    className="block font-sans font-semibold text-3xl sm:text-[42px] text-white no-underline py-3 transition-colors"
                    style={{ lineHeight: 1.2 }}
                    onClick={() => setMobileOpen(false)}
                  >
                    {link.label}
                  </Link>
                </motion.div>
              ))}
              <motion.p
                className="mt-8 font-sans font-normal text-xs"
                style={{ color: 'rgba(255,255,255,0.25)' }}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
              >
                &#9792; Women-Owned &middot; Koye Fache, Addis Ababa
              </motion.p>
            </div>
            <div className="relative flex flex-col items-center justify-center w-[40%]" style={{ background: '#080A0D' }}>
              <LogoMark size={120} />
              <a href="tel:+251911146950" className="mt-6 font-sans font-bold text-xl no-underline" style={{ color: '#E8A020' }}>
                +251 91 114 6950
              </a>
              <p className="mt-2 font-sans text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>
                Mon&ndash;Sat &middot; 24 Hours
              </p>
              <Link
                to="/estimate"
                className="mt-6 w-4/5 text-center font-sans font-bold text-sm no-underline rounded-full py-3 transition-all"
                style={{ background: '#E8A020', color: '#050608' }}
                onClick={() => setMobileOpen(false)}
              >
                Get Free Estimate
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

export { LogoMark };
